import React, { useEffect, useRef, useState } from 'react';
import type { Variants } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import millify from 'millify';
import { gql } from '@soundxyz/gql-string';
import { GetNotifiedButton } from '@components/EmailNotifications/GetNotifiedButton';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { FollowButton } from '@components/basics/FollowButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { Button } from '@components/main-components/button/Button';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { VerifiedTooltip } from '@components/main-components/tooltip/VerifiedTooltip';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import { FollowersModal } from '@components/modals/UserStats/Followers';
import { useMediaQuery } from '@context/mediaQuery';
import { ModalType, useModal } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  BehindTheMusicSectionFragmentDoc,
  getFragment,
  MinimalUserFragmentDoc,
} from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';
import { Fade, SectionTitle } from '../shared';
import { Content, DESKTOP_CONTENT_HEIGHT, FadeContainer, MOBILE_CONTENT_HEIGHT } from './shared';

const AVATAR_SIZE = 40;

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment BehindTheMusicSection on Release {
    id
    behindTheMusic
    artist {
      id
      name
      user {
        id
        ...MinimalUser
        followerCount
        ...FollowersModal
      }
    }
  }
`);

export const BehindTheMusicSection = ({
  releaseFrag,
}: {
  releaseFrag: FragmentType<BehindTheMusicSectionFragmentDoc>;
}) => {
  const { artist, behindTheMusic } = getFragment(BehindTheMusicSectionFragmentDoc, releaseFrag);
  const { avatar, publicAddress, username, webappUri } = getFragment(
    MinimalUserFragmentDoc,
    artist.user,
  );

  const { openModal } = useModal();
  const { tablet } = useMediaQuery();

  const [expanded, setExpanded] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | undefined>(0);

  const contentRef = useRef<HTMLDivElement>(null);

  const maxContentHeight = !tablet ? MOBILE_CONTENT_HEIGHT : DESKTOP_CONTENT_HEIGHT;

  const hide = !!contentHeight && contentHeight > maxContentHeight;

  const artistId = artist.id;
  const artistUserId = artist.user.id;

  const animationVariants: Variants = {
    expand: {
      height: 'auto',
      overflow: 'visible',
      transition: { duration: 0.2, delay: 0.1 },
    },
    collapse: {
      height: hide ? maxContentHeight : 'auto',
      overflow: 'hidden',
      transition: { duration: 0.3, ease: 'easeOut' },
    },
  };

  useEffect(() => {
    setContentHeight(contentRef.current?.scrollHeight);
  }, [contentRef, contentRef.current?.scrollHeight]);

  const hoverCardTrigger = (
    <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
      <div className="flex items-center justify-center gap-3">
        <div className="shrink-0 rounded-sm">
          <Avatar
            src={avatar?.url}
            size={AVATAR_SIZE}
            username={username}
            walletAddress={publicAddress}
            borderRadius={AVATAR_SIZE}
            webappUri={webappUri}
            scaleOnHover
          />
        </div>
      </div>
    </AccessibleLink>
  );

  return (
    <div className="relative">
      <SectionTitle title="Behind the music" />
      <div className="mt-5 flex items-center justify-between gap-7 md:justify-start">
        <div className="flex items-center gap-3">
          <UserHoverCard
            userHoverCardContent={<UserHoverCardContent userId={artistUserId} />}
            trigger={hoverCardTrigger}
          />
          <div className="flex flex-col gap-1">
            <div className="relative flex items-center gap-1">
              <AccessibleLink pathnameUri={webappUriPath(webappUri)}>
                <h3 className="username max-w-[35vw] overflow-hidden text-ellipsis whitespace-nowrap font-base text-base-l font-semibold text-base800 md:max-w-xs">
                  {username}
                </h3>
              </AccessibleLink>

              <VerifiedTooltip portal size={20} noTooltip />
            </div>

            <p
              className="cursor-pointer font-base text-base-s font-normal text-base500"
              onClick={() => {
                openModal(ModalType.FOLLOWERS, {
                  body: <FollowersModal followersData={artist.user} />,
                });
              }}
            >
              {millify(artist.user.followerCount)} followers
            </p>
          </div>
        </div>

        <div className="items-align flex flex-row-reverse gap-2">
          <FollowButton
            userId={artistUserId}
            artistId={artistId}
            size="M"
            variant="primary"
            sourceLocation="Song Page"
          />
          <GetNotifiedButton type="icon_button" artistId={artistId} userId={artistUserId} />
        </div>
      </div>

      <Content
        ref={contentRef}
        animate={expanded ? 'expand' : 'collapse'}
        variants={animationVariants}
        transition={{ ease: 'easeOut' }}
      >
        <p>{behindTheMusic.trim()}</p>
      </Content>
      <AnimatePresence mode="wait">
        {!!hide && !expanded && (
          <FadeContainer
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, delay: 0 }}
          >
            <Fade
              topPercent={tablet ? 48 : 60}
              bottomPercent={tablet ? 99 : 100}
              css={{
                height: `${maxContentHeight}px`,
                position: 'absolute',
                bottom: 0,
                maxWidth: '100%',
              }}
            />
          </FadeContainer>
        )}
      </AnimatePresence>
      {!!hide && (
        <div className="mt-[22px] flex justify-center md:mt-[26px]">
          <Button
            label={!expanded ? 'See more' : 'See less'}
            variant="tertiary"
            onClick={() => setExpanded(prev => !prev)}
            size="M"
            className="min-w-[72px] p-[11px_12px] font-base text-base-m font-semibold text-base800"
          />
        </div>
      )}
    </div>
  );
};
